import { Editor, EditorContent, EditorContext, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { TextStyleKit, Color } from "@tiptap/extension-text-style";
import { TextAlign } from "@tiptap/extension-text-align";
import Link from "@tiptap/extension-link";
import { useMemo, forwardRef, useImperativeHandle, useEffect } from "react";
import { chakra } from "@chakra-ui/react";
import { BodyDoc } from "@common/interfaces/KYDCampaign";
import { MenuBar } from "./MenuBar";
import { BubbleMenuComponent, type BubbleMenuConfig } from "./BubbleMenu";

const ChakraEditor = chakra(EditorContent);

interface TipTapEditorProps {
  value: BodyDoc;
  onChange: (value: BodyDoc) => void;
  isDisabled?: boolean;
  bubbleMenuConfig?: BubbleMenuConfig;
  showBlockSelector?: boolean;
  leftContent?: React.ReactNode;
}

const TipTapEditor = forwardRef<Editor, TipTapEditorProps>(
  (
    {
      value,
      onChange,
      isDisabled = false,
      bubbleMenuConfig,
      showBlockSelector = true,
      leftContent,
    },
    ref
  ) => {
    const editor = useEditor({
      editable: !isDisabled,
      editorProps: {
        attributes: {
          class: "editor-container",
        },
        handleClick: (view, pos, event) => {
          // Prevent link clicks from opening in new page
          const target = event.target as HTMLElement;
          if (target.tagName === "A" || target.closest("a")) {
            event.preventDefault();
            event.stopPropagation();
            return true; // Handle the click ourselves
          }
          return false; // Let TipTap handle other clicks
        },
      },
      extensions: [
        StarterKit,
        TextStyleKit,
        Color,
        TextAlign.configure({
          types: ["heading", "paragraph"],
        }),
        Link.configure({
          openOnClick: false,
          autolink: true,
          defaultProtocol: "https",
          protocols: ["http", "https"],
          isAllowedUri: (url, ctx) => {
            try {
              // construct URL
              const parsedUrl = url.includes(":")
                ? new URL(url)
                : new URL(`${ctx.defaultProtocol}://${url}`);

              // use default validation
              if (!ctx.defaultValidate(parsedUrl.href)) {
                return false;
              }

              // disallowed protocols
              const disallowedProtocols = ["ftp", "file", "mailto"];
              const protocol = parsedUrl.protocol.replace(":", "");

              if (disallowedProtocols.includes(protocol)) {
                return false;
              }

              // only allow protocols specified in ctx.protocols
              const allowedProtocols = ctx.protocols.map((p) =>
                typeof p === "string" ? p : p.scheme
              );

              if (!allowedProtocols.includes(protocol)) {
                return false;
              }

              // disallowed domains
              const disallowedDomains = [];
              const domain = parsedUrl.hostname;

              if (disallowedDomains.includes(domain)) {
                return false;
              }

              // all checks have passed
              return true;
            } catch {
              return false;
            }
          },
          shouldAutoLink: (url) => {
            try {
              // construct URL
              const parsedUrl = url.includes(":")
                ? new URL(url)
                : new URL(`https://${url}`);

              // only auto-link if the domain is not in the disallowed list
              const disallowedDomains = [];
              const domain = parsedUrl.hostname;

              return !disallowedDomains.includes(domain);
            } catch {
              return false;
            }
          },
          HTMLAttributes: {
            style: {
              textDecoration: "underline",
              color: "blue",
              pointerEvents: "none",
            },
            className: "editor-link",
          },
        }),
      ],
      content: value,

      onUpdate: ({ editor }) => {
        onChange(editor.getJSON());
      },
    });

    useEffect(() => {
      if (isDisabled) {
        editor.setEditable(false);
      } else {
        editor.setEditable(true);
      }
    }, [isDisabled, editor]);

    // Expose the editor instance through the ref
    useImperativeHandle(ref, () => editor as Editor, [editor]);

    // Memoize the provider value to avoid unnecessary re-renders
    const providerValue = useMemo(() => ({ editor }), [editor]);

    return (
      <EditorContext.Provider value={providerValue}>
        <MenuBar
          editor={editor}
          showBlockSelector={showBlockSelector}
          leftContent={leftContent}
        />
        <BubbleMenuComponent editor={editor} config={bubbleMenuConfig} />
        <ChakraEditor
          className="editor-container"
          cursor="text"
          minH="100px"
          w="full"
          p={2}
          editor={editor}
          autoFocus={true}
        />
      </EditorContext.Provider>
    );
  }
);

TipTapEditor.displayName = "TipTapEditor";

export default TipTapEditor;
